import { useEffect, useRef, useState, useCallback } from 'react'
import { useFocusNavigationDep, useControllerInputDep } from '../components/providers/FocusNavigationProvider/FocusNavigationProvider'
import { useSignals } from '@preact/signals-react/runtime'

export interface UseListNavigationOptions {
  /** Focused index to reset to when the list becomes active (default 0). */
  initialIndex?: number
  /**
   * Override the default prev/next stepping (up/left = prev, down/right = next).
   * Lets an otherwise-flat list support a 2D-ish layout - e.g. a row of tabs where
   * down "drills into" the active tab's content instead of stepping to the next tab.
   * Falls back to the default stepping when omitted.
   */
  computeNextIndex?: (current: number, direction: 'up' | 'down' | 'left' | 'right', itemsLength: number) => number
}

/**
 * Generic hook for navigating through a list of items with focus navigation.
 *
 * @param items - Array of items to navigate through
 * @param onSelect - Callback when an item is selected
 * @param isActive - Whether this list navigation should be active (e.g., when menu is open)
 * @param onNavigate - Optional callback when navigation occurs (for custom handling)
 * @param skipFocusCheck - If true, always handle events when active (useful for dialogs/menus)
 * @param options - Optional initial index / custom stepping (see UseListNavigationOptions)
 */
export function useListNavigation<T>(
  items: T[],
  onSelect: (item: T, index: number) => void,
  isActive: boolean = false,
  onNavigate?: (index: number) => void,
  skipFocusCheck: boolean = false,
  options: UseListNavigationOptions = {}
) {
  useSignals()
  const focusNavigation = useFocusNavigationDep()
  const controllerInput = useControllerInputDep()
  const isFocusNavActive = focusNavigation.isActive.value
  const initialIndex = options.initialIndex ?? 0
  const { computeNextIndex } = options
  const [focusedIndex, setFocusedIndex] = useState<number>(initialIndex)
  const itemRefs = useRef<(HTMLElement | null)[]>([])

  // Reset focus when list becomes active
  // For dialogs, allow navigation regardless of focus area
  useEffect(() => {
    if (isActive && isFocusNavActive && items.length > 0) {
      setFocusedIndex(initialIndex)
    } else if (!isActive) {
      setFocusedIndex(-1)
    }
  }, [isActive, isFocusNavActive, items.length, initialIndex])

  const handleNavigate = useCallback((direction: 'up' | 'down' | 'left' | 'right') => {
    if (!isActive || !isFocusNavActive || items.length === 0) return

    setFocusedIndex((current) => {
      // Focus can move outside this hook (mouse clicks, browser Tab navigation,
      // or a parent list handing focus into its content). Prefer the actual DOM
      // focus target so a directional input always advances from the visible row.
      const activeElement = document.activeElement
      const activeIndex = itemRefs.current.findIndex((ref) => ref === activeElement || ref?.contains(activeElement))
      const focused = activeIndex === -1 ? current : activeIndex

      if (focused === -1) {
        return 0
      }

      let next = focused
      if (computeNextIndex) {
        next = computeNextIndex(focused, direction, items.length)
      } else {
        switch (direction) {
          case 'left':
          case 'up':
            // For vertical lists, up goes to previous item
            next = focused > 0 ? focused - 1 : items.length - 1
            break
          case 'right':
          case 'down':
            // For vertical lists, down goes to next item
            next = focused < items.length - 1 ? focused + 1 : 0
            break
        }
      }

      if (onNavigate) {
        onNavigate(next)
      }

      return next
    })
  }, [isActive, isFocusNavActive, items.length, onNavigate, computeNextIndex])

  const handleSelect = useCallback(() => {
    if (!isActive || !isFocusNavActive || focusedIndex < 0 || focusedIndex >= items.length) return

    onSelect(items[focusedIndex], focusedIndex)
  }, [isActive, isFocusNavActive, focusedIndex, items, onSelect])

  // Subscribe to semantic input events
  useEffect(() => {
    if (!isActive || !isFocusNavActive) return

    const unsubscribe = controllerInput.subscribe((event) => {
      // For dialogs/menus (skipFocusCheck=true), always handle events when active
      // For regular lists, only handle if focus is within this list's items
      if (!skipFocusCheck) {
        const activeElement = document.activeElement
        const isListFocused = itemRefs.current.some(ref => ref === activeElement) ||
                             (activeElement && itemRefs.current.some(ref => ref?.contains(activeElement)))

        if (!isListFocused && focusedIndex >= 0) {
          return
        }
      }

      switch (event.type) {
        case 'navigate':
          handleNavigate(event.direction)
          break
        case 'select':
          handleSelect()
          break
      }
    })
      return unsubscribe
    }, [isActive, isFocusNavActive, controllerInput, focusedIndex, handleNavigate, handleSelect, skipFocusCheck])

  // Focus the element when focusedIndex changes
  useEffect(() => {
    if (isActive && isFocusNavActive && focusedIndex >= 0 && itemRefs.current[focusedIndex]) {
      itemRefs.current[focusedIndex]?.focus()
    }
  }, [isActive, isFocusNavActive, focusedIndex])

  const registerItem = useCallback((index: number, element: HTMLElement | null) => {
    if (element) {
      itemRefs.current[index] = element
    } else {
      itemRefs.current[index] = null
    }
  }, [])

  return {
    focusedIndex,
    registerItem,
  }
}
